import React, { useState } from 'react';

import { Web } from '@mui/icons-material';
import { Form, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Breadcrumbs, Button, CheckboxRow, FormTextField, InfoTextField, SubHeader } from '../../../../../components';
import { searchExpenseById, saveDeletedExpense, getErrorMessage } from '../operations';

import './styles.scoped.css';

const DeletedExpense = () => {
  const navigate = useNavigate();
  const userId = useSelector(state => state.admin.user.id);

  const [expenseId, setExpenseId] = useState('');
  const [expense, setExpense] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [searchError, setSearchError] = useState('');

  const [clearApprovedFlag, setClearApprovedFlag] = useState(false);
  const [statusReadyToBill, setStatusReadyToBill] = useState(false);
  const [clearPayrollDate, setClearPayrollDate] = useState(false);
  const [notes, setNotes] = useState('');

  // Per-field validation errors, surfaced under each checkbox via its own
  // error / errorMessage props (the CP-787 ManualExpense pattern).
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldError = key => setFieldErrors(prev => (prev[key] ? { ...prev, [key]: undefined } : prev));

  const handleSearch = async () => {
    if (!expenseId.trim()) {
      setSearchError('Please enter an Expense ID.');
      return;
    }
    setSearching(true);
    setSearchError('');
    setExpense(null);
    try {
      const result = await searchExpenseById(expenseId.trim());
      if (result) {
        setExpense(result);
        setNotes(result.notes || '');
      } else {
        setSearchError('Expense not found.');
      }
    } catch {
      setSearchError('Error searching for expense.');
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async () => {
    // Save is always enabled; if no expense has been loaded yet, prompt for the
    // Expense ID inline (under that field) rather than blocking via a disabled button.
    if (!expense) {
      setSearchError('Please enter an Expense ID.');
      return;
    }
    // Non-billable expenses must never be set to a billable status — doing so is
    // the exact desync CP-810 fixed in the edit/void flows. Hard stop here.
    if (expense.nonBillable) {
      setSaveError('This expense is non-billable and cannot be set to Ready to Bill.');
      return;
    }
    // The CP-788 flow requires all three boxes — show the error under each unchecked one.
    const nextErrors = {};
    if (!clearApprovedFlag) nextErrors.clearApprovedFlag = 'Required before saving.';
    if (!statusReadyToBill) nextErrors.statusReadyToBill = 'Required before saving.';
    if (!clearPayrollDate) nextErrors.clearPayrollDate = 'Required before saving.';
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setSaving(true);
    setSaveError('');
    try {
      await saveDeletedExpense({ expenseId, userId, clearApprovedFlag, statusReadyToBill, clearPayrollDate, notes });
      navigate('/expense-entry/expense-maintenance');
    } catch (e) {
      setSaveError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SubHeader
        text="Expenses"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Expenses?"
      />
      <Breadcrumbs
        leftTitle="Expense Maintenance"
        rightTitle="Edge Deleted Expense"
        route="/expense-entry/expense-maintenance"
      />
      <div className="wrap">
        <div className="maintenance-card">
          <div className="card-tab">Set Transmitted Expense to Ready to Bill</div>
          <div className="card-content">
            <div className="search-row">
              <FormTextField
                label="Enter Expense ID"
                value={expenseId}
                onChange={e => {
                  setExpenseId(e.target.value);
                  setSearchError('');
                }}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                error={!!searchError}
                errorMessage={searchError}
                disabled={searching}
              />
              {searching ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Button variant="dark" size="lg" onPress={handleSearch} text="Search" />
              )}
            </div>

            {expense && (
              <>
                <div className="expense-details-grid">
                  <InfoTextField
                    label="Driver Name & Driver Number"
                    required
                    value={`${expense.driverName} - ${expense.driverId}`}
                  />
                  <InfoTextField label="Total To Be Billed" value={expense.totalToBeBilled} />
                  <InfoTextField label="Code" value={expense.code} />
                  <InfoTextField label="Status" value={expense.status} />
                </div>
                <InfoTextField
                  label="Customer Name & Customer Number"
                  required
                  value={`${expense.customerName} - ${expense.customerId}`}
                />

                {expense.status !== 'Transmitted' && (
                  <p className="error-text">
                    Warning: this expense has status &quot;{expense.status}&quot; — expected &quot;Transmitted&quot;.
                  </p>
                )}
                {expense.nonBillable && (
                  <p className="error-text">
                    This expense is non-billable. It cannot be set to Ready to Bill.
                  </p>
                )}

                <div className="inner-section">
                  <CheckboxRow
                    label='Clear flag on "Approved to Transmit"'
                    checked={clearApprovedFlag}
                    onChange={e => {
                      setClearApprovedFlag(e.target.checked);
                      clearFieldError('clearApprovedFlag');
                    }}
                    error={!!fieldErrors.clearApprovedFlag}
                    errorMessage={fieldErrors.clearApprovedFlag}
                  />
                  <CheckboxRow
                    label="Change Status to Ready to Bill"
                    checked={statusReadyToBill}
                    onChange={e => {
                      setStatusReadyToBill(e.target.checked);
                      clearFieldError('statusReadyToBill');
                    }}
                    error={!!fieldErrors.statusReadyToBill}
                    errorMessage={fieldErrors.statusReadyToBill}
                  />
                  <CheckboxRow
                    label="Clear Payroll Week Ending Date"
                    checked={clearPayrollDate}
                    onChange={e => {
                      setClearPayrollDate(e.target.checked);
                      clearFieldError('clearPayrollDate');
                    }}
                    error={!!fieldErrors.clearPayrollDate}
                    errorMessage={fieldErrors.clearPayrollDate}
                  />
                  <Form.Group style={{ marginTop: '12px' }}>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control as="textarea" rows={4} value={notes} onChange={e => setNotes(e.target.value)} />
                  </Form.Group>
                </div>
              </>
            )}
          </div>
          {/* Form-wide save/server error only — per-field prompts show under their field. */}
          {saveError && <p className="error-text save-error">{saveError}</p>}
          <div className="buttons-container">
            <Button variant="primary" size="lg" onPress={handleSave} text="Save" disabled={saving} />
            <Button
              variant="outline"
              size="lg"
              onPress={() => navigate('/expense-entry/expense-maintenance')}
              text="Cancel"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default DeletedExpense;
